import { useCallback, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

import { useGetLegacyInsuranceCarriersQuery } from '@/api/hooks/queries/use-get-legacy-insurance-carriers-query';
import { Button } from '@/components/ui/button.tsx';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select.tsx';
import { insuranceStateOptions } from '@/lib/zipcode.ts';
import {
  type CarrierType,
  type InsuranceCarrier,
  type QueuedInsurance,
  type UnprovidedInsurance,
  groupByType,
} from '@/types/insurance.type.ts';

const formId = 'insurance-edit-form';
const formSchema = z.object({
  state: z.string().min(1, 'Please make a selection'),
  internalId: z.string().min(1, 'Please make a selection'),
  memberId: z.string().min(1, 'Member ID is required'),
});
type FormData = z.infer<typeof formSchema>;

interface Props {
  initialData: FormData;
  carrierType: CarrierType;
  onSubmitChanges: (insurance: QueuedInsurance) => void;
  onConfirm: () => void;
  onCannotProvide: (insurance: UnprovidedInsurance) => void;
}

export function InsuranceEditForm({
  initialData,
  carrierType,
  onSubmitChanges,
  onConfirm,
  onCannotProvide,
}: Props) {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData,
  });

  const [state, internalId, memberId] = form.watch(['state', 'internalId', 'memberId']);

  const { data: carriers = [], isLoading, isSuccess } = useGetLegacyInsuranceCarriersQuery(state);
  const carriersByType = useMemo(() => groupByType(carriers), [carriers]);

  const carrierOptions = useMemo(
    () => carriersByType.get(carrierType) ?? [],
    [carriersByType, carrierType]
  );

  const plansNotReady = !isSuccess || state === '';
  const anythingChanged = useMemo(
    () =>
      initialData.state !== state ||
      initialData.memberId !== memberId ||
      initialData.internalId !== internalId,
    [initialData, state, memberId, internalId]
  );

  useEffect(
    function removeInvalidCarriers() {
      if (isSuccess) {
        const internalIdLost = carriers.every((c) => c.internalId !== internalId);
        if (internalIdLost) form.setValue('internalId', '');
      }
    },
    [isSuccess, carriers, internalId, carrierType, form]
  );

  const resetForm = useCallback(() => {
    form.setValue('state', initialData.state);
    form.setValue('internalId', initialData.internalId);
    form.setValue('memberId', initialData.memberId);
  }, [form, initialData]);

  const getSelectedCarrier = useCallback(
    (internalId: string) => {
      return carriers.find((c) => c.internalId === internalId);
    },
    [carriers]
  );

  const submitInsurance = useCallback(
    (data: FormData) => {
      const carrier = getSelectedCarrier(data.internalId);
      if (!carrier) throw new Error("Selected carrier that doesn't exist.");
      onSubmitChanges({ carrier, memberId: data.memberId });
    },
    [getSelectedCarrier, onSubmitChanges]
  );

  const cannotProvide = useCallback(() => {
    const carrier = getSelectedCarrier(internalId);
    const descriptiveMemberId = memberId === '' ? 'Not-provided' : memberId;
    onCannotProvide({ carrierType, carrier, memberId: descriptiveMemberId });
  }, [getSelectedCarrier, internalId, carrierType, memberId, onCannotProvide]);

  return (
    <form
      onSubmit={form.handleSubmit(submitInsurance)}
      id={formId}
      className="grid grid-cols-1 gap-8 sm:gap-12"
    >
      <FieldGroup>
        <Controller
          name="state"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-state`}>State</FieldLabel>
              <NativeSelect
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e);
                  form.setValue('internalId', '');
                  form.setValue('memberId', '');
                }}
                id={`${formId}-state`}
                aria-invalid={fieldState.invalid}
              >
                <NativeSelectOption value="">Select...</NativeSelectOption>
                {insuranceStateOptions.map((option) => (
                  <NativeSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>

              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="internalId"
          control={form.control}
          render={({ field, fieldState }) => {
            const selectedCarrier =
              carrierOptions.find((c) => c.internalId === field.value) ?? null;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${formId}-internalId`}>Plan name</FieldLabel>
                <Combobox
                  items={carrierOptions}
                  itemToStringLabel={(carrier) => carrier.displayName}
                  value={selectedCarrier}
                  onValueChange={(carrier) => {
                    field.onChange(carrier?.internalId ?? '');
                  }}
                  disabled={plansNotReady}
                >
                  <ComboboxInput
                    placeholder={isLoading ? 'Loading...' : 'Select...'}
                    id={`${formId}-internalId`}
                    aria-label="Plan name"
                    aria-invalid={fieldState.invalid}
                    aria-busy={isLoading}
                    aria-describedby={isLoading ? `${formId}-internalId-loading` : undefined}
                    disabled={plansNotReady}
                  />
                  <ComboboxContent>
                    <ComboboxEmpty>No carriers found matching that keyword</ComboboxEmpty>
                    <ComboboxList>
                      {(carrier: InsuranceCarrier) => (
                        <ComboboxItem key={carrier.internalId} value={carrier}>
                          {carrier.name}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
                {isLoading && (
                  <span id={`${formId}-internalId-loading`} className="sr-only" aria-live="polite">
                    Loading insurance plans...
                  </span>
                )}

                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            );
          }}
        />

        <Controller
          name="memberId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-memberId`}>Member ID</FieldLabel>
              <Input
                {...field}
                placeholder="(e.g., AA11111A)"
                id={`${formId}-memberId`}
                aria-invalid={fieldState.invalid}
                autoComplete="off"
              />

              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      <div className="w-full space-y-8 justify-self-center sm:w-auto sm:space-y-4.5 sm:justify-self-start">
        {anythingChanged ? (
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <Button variant="ghost" onClick={resetForm}>
              Reset
            </Button>
            <Button type="submit">Verify again</Button>
          </div>
        ) : (
          <Button className="w-full sm:w-auto" onClick={onConfirm}>
            Looks correct
          </Button>
        )}

        <Button
          className="justify-self-center sm:justify-self-start sm:px-0"
          onClick={cannotProvide}
          size="sm"
          variant="link"
        >
          I don't have my insurance info
        </Button>
      </div>
    </form>
  );
}
